/**
 * POS Sales API Service
 *
 * Client for POS sales endpoints.
 */

import { apiClient } from '@/api/client';
import type {
  SyncResult,
  SalesSyncStatus,
  PosReceiptsListResponse,
  PosReceiptDetailResponse,
  SalesImportBatchesListResponse,
  ProductSaleEventsResponse,
  ConsumptionSummaryResponse,
  ConsumptionByProductResponse,
  ConsumptionByCategoryResponse,
  PosReceiptsQueryParams,
  SalesImportBatchesQueryParams,
  ProductSaleEventsQueryParams,
  ConsumptionQueryParams,
  SalesImportBatchListItem,
} from '@bstock/shared';

// =============================================================================
// SYNC API
// =============================================================================

export async function syncSales(range?: { from: string; to: string }): Promise<SyncResult> {
  const response = await apiClient.post<{ success: boolean; data: SyncResult }>(
    '/sales/sync',
    range
  );
  return response.data.data;
}

/**
 * Re-synchronisation ciblee par posReceiptId Bill (ex "T-00220027").
 * Utilise par le bouton "Resynchroniser ce ticket" sur le drawer de detail.
 * Le backend cree un batch MANUAL_RESYNC et appelle Bill avec posReceiptIds[].
 */
export async function syncByReceiptIds(receiptIds: string[]): Promise<{
  batchId: string;
  receiptsImported: number;
  itemsImported: number;
  chunksProcessed: number;
  targetReceiptIdsCount: number;
}> {
  const response = await apiClient.post<{ success: boolean; data: {
    batchId: string;
    receiptsImported: number;
    itemsImported: number;
    chunksProcessed: number;
    targetReceiptIdsCount: number;
  } }>(
    '/sales/sync/by-ids',
    { receiptIds }
  );
  return response.data.data;
}

export async function initSync(startDate: string): Promise<{ batchId: string }> {
  const response = await apiClient.post<{ success: boolean; data: { batchId: string } }>(
    '/sales/sync/init',
    { startDate }
  );
  return response.data.data;
}

export async function getSyncStatus(): Promise<SalesSyncStatus> {
  const response = await apiClient.get<{ success: boolean; data: SalesSyncStatus }>(
    '/sales/sync/status'
  );
  return response.data.data;
}

// =============================================================================
// IMPORT BATCHES API
// =============================================================================

export async function listBatches(
  params: SalesImportBatchesQueryParams
): Promise<SalesImportBatchesListResponse> {
  const response = await apiClient.get<{ success: boolean; data: SalesImportBatchesListResponse }>(
    '/sales/import/batches',
    { params }
  );
  return response.data.data;
}

export async function getBatch(id: string): Promise<SalesImportBatchListItem> {
  const response = await apiClient.get<{ success: boolean; data: SalesImportBatchListItem }>(
    `/sales/import/batches/${id}`
  );
  return response.data.data;
}

export async function deleteBatch(id: string): Promise<void> {
  await apiClient.delete(`/sales/import/batches/${id}`);
}

// =============================================================================
// RECEIPTS API
// =============================================================================

export async function listReceipts(
  params: PosReceiptsQueryParams
): Promise<PosReceiptsListResponse> {
  const response = await apiClient.get<{ success: boolean; data: PosReceiptsListResponse }>(
    '/pos-receipts',
    { params }
  );
  return response.data.data;
}

export async function getReceipt(id: string): Promise<PosReceiptDetailResponse> {
  const response = await apiClient.get<{ success: boolean; data: PosReceiptDetailResponse }>(
    `/pos-receipts/${id}`
  );
  return response.data.data;
}

export async function getProductSaleEvents(
  params: ProductSaleEventsQueryParams
): Promise<ProductSaleEventsResponse> {
  const response = await apiClient.get<{ success: boolean; data: ProductSaleEventsResponse }>(
    '/pos-receipts/by-product',
    { params }
  );
  return response.data.data;
}

// =============================================================================
// CONSUMPTION REPORT API
// =============================================================================

export async function getConsumptionSummary(
  params: ConsumptionQueryParams
): Promise<ConsumptionSummaryResponse> {
  const response = await apiClient.get<{ success: boolean; data: ConsumptionSummaryResponse }>(
    '/theoretical-consumption/summary',
    { params }
  );
  return response.data.data;
}

export async function getConsumptionByProduct(
  rawProductId: string,
  params: { startDate: string; endDate: string; siteId?: string; page?: number; limit?: number }
): Promise<ConsumptionByProductResponse> {
  const response = await apiClient.get<{ success: boolean; data: ConsumptionByProductResponse }>(
    `/theoretical-consumption/by-product/${rawProductId}`,
    { params }
  );
  return response.data.data;
}

export async function getConsumptionByCategory(
  params: { startDate: string; endDate: string; siteId?: string }
): Promise<ConsumptionByCategoryResponse> {
  const response = await apiClient.get<{ success: boolean; data: ConsumptionByCategoryResponse }>(
    '/theoretical-consumption/by-category',
    { params }
  );
  return response.data.data;
}

// Export as namespace
export const posSalesService = {
  syncSales,
  syncByReceiptIds,
  initSync,
  getSyncStatus,
  listBatches,
  getBatch,
  deleteBatch,
  listReceipts,
  getReceipt,
  getProductSaleEvents,
  getConsumptionSummary,
  getConsumptionByProduct,
  getConsumptionByCategory,
};
